import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { shippixService } from '../../services/shippixService';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  Container,
  InputLabel,
  InputAdornment,
  Alert,
  Chip
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Scale as ScaleIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Refresh as RefreshIcon,
  Euro as EuroIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { format, subMonths, startOfYear, endOfYear, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Package, Weight, Box as BoxIcon, Euro, RefreshCcw, Calendar as CalendarIconLucide, TrendingUp, MapPin, } from 'lucide-react';

// Corrigir os ícones do Leaflet
// delete (L.Icon.Default.prototype as any)._getIconUrl;
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
//   iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
//   shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
// });

type FilterPeriod = 'monthly' | 'current_quarter' | 'previous_quarter' | 'semiannual' | 'annual';

interface Metrics {
  totalShipments: number;
  averageWeight: number;
  averageVolumes: number;
  averagePrice: number;
  totalExpense: number;
  trends: {
    shipments: { value: number, direction: 'up' | 'down', previousValue: number };
    weight: { value: number, direction: 'up' | 'down', previousValue: number };
    volumes: { value: number, direction: 'up' | 'down', previousValue: number };
    expense: { value: number, direction: 'up' | 'down', previousValue: number };
    price: { value: number, direction: 'up' | 'down', previousValue: number };
  };
}

const CHART_COLORS = [
  '#4CAF50', // Verde
  '#2196F3', // Azul
  '#FFC107', // Amarelo
  '#9C27B0', // Roxo
  '#F44336', // Vermelho
  '#FF9800', // Laranja
  '#3F51B5', // Indigo
  '#009688', // Verde água
  '#E91E63', // Rosa
  '#CDDC39', // Lima
  '#00BCD4', // Ciano
  '#FF5722', // Laranja escuro
  '#795548', // Marrom
  '#607D8B', // Azul acinzentado
  '#673AB7', // Roxo escuro
  '#FF69B4', // Rosa claro
  '#8BC34A', // Verde limão
  '#03A9F4', // Azul claro
  '#FFC67D', // Laranja claro
  '#8E24AA', // Violeta
  '#4CAF50', // Verde claro
  '#F7DC6F'  // Amarelo claro
];

const chartTheme = {
  background: 'transparent',
  textColor: '#666666',
  fontSize: 11,
  axis: {
    domain: {
      line: {
        stroke: '#dddddd',
        strokeWidth: 1
      }
    },
    ticks: {
      line: {
        stroke: '#dddddd',
        strokeWidth: 1
      }
    }
  },
  grid: {
    line: {
      stroke: '#dddddd',
      strokeWidth: 1
    }
  }
};

// Componente para o gráfico de barras
const OperatorBarChart = ({ data }) => (
  <ResponsiveBar
    data={data}
    keys={['value']}
    indexBy="label"
    margin={{ top: 20, right: 20, bottom: 120, left: 60 }}
    padding={0.3}
    valueScale={{ type: 'linear' }}
    indexScale={{ type: 'band', round: true }}
    colors={CHART_COLORS}
    theme={chartTheme}
    borderRadius={4}
    borderWidth={0}
    enableLabel={true}
    labelSkipWidth={12}
    labelSkipHeight={12}
    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
    axisBottom={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: -45,
      legend: 'Operador',
      legendPosition: 'middle',
      legendOffset: 85
    }}
    axisLeft={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: 'Quantidade de Envios',
      legendPosition: 'middle',
      legendOffset: -50
    }}
    enableGridY={false}
    animate={true}
    motionStiffness={90}
    motionDamping={15}
    tooltip={({ id, value, color }) => (
      <Box sx={{ 
        p: 1, 
        background: 'white', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {id}
          </Typography>
        </Box>
        <Typography variant="body2">
          Envios: {value}
        </Typography>
      </Box>
    )}
  />
);

// Componente para o gráfico de barras do custo médio
const OperatorAverageCostChart = ({ data }) => (
  <ResponsiveBar
    data={data}
    keys={['value']}
    indexBy="label"
    margin={{ top: 20, right: 20, bottom: 120, left: 60 }}
    padding={0.3}
    valueScale={{ type: 'linear' }}
    indexScale={{ type: 'band', round: true }}
    colors={({ data }) => data.color}
    theme={chartTheme}
    borderRadius={4}
    borderWidth={0}
    enableLabel={true}
    labelSkipWidth={12}
    labelSkipHeight={12}
    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
    axisBottom={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: -45,
      legend: 'Operador',
      legendPosition: 'middle',
      legendOffset: 85
    }}
    axisLeft={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: 'Custo Médio (€)',
      legendPosition: 'middle',
      legendOffset: -50
    }}
    enableGridY={false}
    animate={true}
    motionStiffness={90}
    motionDamping={15}
    valueFormat={value => `${value.toFixed(2)}€`}
    tooltip={({ id, value, color }) => (
      <Box sx={{ 
        p: 1, 
        background: 'white', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {id}
          </Typography>
        </Box>
        <Typography variant="body2">
          Custo Médio: {value.toFixed(2)}€
        </Typography>
      </Box>
    )}
  />
);

// Componente para o gráfico de pizza
const CityPieChart = ({ data }) => (
  <ResponsivePie
    data={data.slice(0, 20)}
    margin={{ top: 20, right: 180, bottom: 20, left: 20 }}
    innerRadius={0.5}
    padAngle={0.7}
    cornerRadius={3}
    activeOuterRadiusOffset={8}
    colors={CHART_COLORS}
    theme={chartTheme}
    borderWidth={1}
    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
    enableArcLinkLabels={true}
    arcLinkLabelsSkipAngle={0}
    arcLinkLabelsTextColor="#333333"
    arcLinkLabelsThickness={2}
    arcLinkLabelsColor={{ from: 'color' }}
    arcLinkLabelsDiagonalLength={12}
    arcLinkLabelsStraightLength={12}
    arcLinkLabelsTextOffset={4}
    arcLinkLabelsFont={{
      size: 12,
      weight: 600
    }}
    arcLabelsSkipAngle={10}
    arcLabelsTextColor="#ffffff"
    animate={true}
    motionStiffness={90}
    motionDamping={15}
    tooltip={({ datum: { id, value, color } }) => (
      <Box sx={{ 
        p: 1, 
        background: 'white', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {id}
        </Typography>
        <Typography variant="body2" sx={{ color }}>
          {value} envios
        </Typography>
      </Box>
    )}
    legends={[
      {
        anchor: 'right',
        direction: 'column',
        justify: false,
        translateX: 140,
        translateY: 0,
        itemsSpacing: 4,
        itemWidth: 140,
        itemHeight: 18,
        itemDirection: 'left-to-right',
        itemOpacity: 0.85,
        symbolSize: 10,
        symbolShape: 'circle',
        itemTextColor: '#666666',
        effects: [
          {
            on: 'hover',
            style: {
              itemOpacity: 1,
              itemTextColor: '#333333'
            }
          }
        ]
      }
    ]}
  />
);

const metricCardStyles = {
  paper: {
    p: 3,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    background: 'linear-gradient(135deg, rgba(59,130,246,0.03) 0%, rgba(16,185,129,0.03) 100%)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: (theme) => theme.shadows[4]
    }
  },
  icon: {
    p: 1.5,
    borderRadius: 2,
    marginBottom: 2,
    display: 'inline-flex',
    background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
    color: 'white'
  }
};

export default function ShippingReport() {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [period, setPeriod] = useState<FilterPeriod>('monthly');
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [metrics, setMetrics] = useState<Metrics>({
    totalShipments: 0,
    averageWeight: 0,
    averageVolumes: 0,
    averagePrice: 0,
    totalExpense: 0,
    trends: {
      shipments: { value: 0, direction: 'up', previousValue: 0 },
      weight: { value: 0, direction: 'up', previousValue: 0 },
      volumes: { value: 0, direction: 'up', previousValue: 0 },
      expense: { value: 0, direction: 'up', previousValue: 0 },
      price: { value: 0, direction: 'up', previousValue: 0 }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [filteredShipments, setFilteredShipments] = useState<any[]>([]);
  const [operatorChartData, setOperatorChartData] = useState<any[]>([]);
  const [cityChartData, setCityChartData] = useState<any[]>([]);
  const [operatorAverageCostData, setOperatorAverageCostData] = useState<any[]>([]);

  // Array de cores fixas para os operadores
  const OPERATOR_COLORS = [
    '#2196F3', // Azul
    '#4CAF50', // Verde
    '#FFC107', // Amarelo
    '#9C27B0', // Roxo
    '#F44336', // Vermelho
    '#FF9800', // Laranja
    '#3F51B5', // Indigo
    '#009688', // Verde água
    '#E91E63', // Rosa
    '#CDDC39', // Lima
    '#00BCD4', // Ciano
    '#FF5722', // Laranja escuro
    '#795548', // Marrom
    '#607D8B', // Azul acinzentado
    '#673AB7'  // Roxo escuro
  ];

  // Função para normalizar nome da cidade
  const normalizeCity = (city: string): string => {
    if (!city) return 'Desconhecido';
    
    const normalized = city.trim().toUpperCase();
    
    // Mapeamento de nomes alternativos
    const cityMap: Record<string, string> = {
      'VILA CHA': 'VILA CHÃ',
      'VILA CHA VCD': 'VILA CHÃ',
      'VILA CHÃ VCD': 'VILA CHÃ',
      'GAIA': 'VILA NOVA DE GAIA',
      'FANZERES': 'FÂNZERES',
      'PORTO': 'PORTO',
      'MATOSINHOS': 'MATOSINHOS',
      'MAIA': 'MAIA',
      'VALONGO': 'VALONGO',
      'ERMESINDE': 'ERMESINDE',
      'LISBOA': 'LISBOA',
      'BRAGA': 'BRAGA',
      'COIMBRA': 'COIMBRA',
      'AVEIRO': 'AVEIRO'
    };

    return cityMap[normalized] || normalized;
  };

  const calculateTrend = (current: number, previous: number): { value: number, direction: 'up' | 'down', previousValue: number } => {
    if (previous === 0) return { value: 0, direction: 'up', previousValue: previous };
    
    const percentageChange = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(Math.round(percentageChange)),
      direction: percentageChange >= 0 ? 'up' : 'down',
      previousValue: previous
    };
  };

  const getStartDate = (selectedPeriod: FilterPeriod): Date => {
    const now = new Date();
    const startDate = new Date();

    switch (selectedPeriod) {
      case 'monthly':
        startDate.setMonth(now.getMonth());
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'current_quarter':
        const currentQuarter = Math.floor(now.getMonth() / 3);
        startDate.setMonth(currentQuarter * 3);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'previous_quarter':
        const prevQuarter = Math.floor(now.getMonth() / 3) - 1;
        if (prevQuarter < 0) {
          startDate.setFullYear(now.getFullYear() - 1);
          startDate.setMonth(9); // Começa no mês 9 (outubro) do ano anterior
        } else {
          startDate.setMonth(prevQuarter * 3);
        }
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'semiannual':
        const currentMonth = now.getMonth();
        startDate.setMonth(currentMonth >= 6 ? 6 : 0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'annual':
        startDate.setMonth(0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
    }

    return startDate;
  };

  const getEndDate = (selectedPeriod: FilterPeriod): Date => {
    const now = new Date();
    const endDate = new Date();

    switch (selectedPeriod) {
      case 'monthly':
        endDate.setMonth(now.getMonth());
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'current_quarter':
        const currentQuarter = Math.floor(now.getMonth() / 3);
        endDate.setMonth((currentQuarter * 3) + 3);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'previous_quarter':
        const prevQuarter = Math.floor(now.getMonth() / 3) - 1;
        if (prevQuarter < 0) {
          endDate.setFullYear(now.getFullYear() - 1);
          endDate.setMonth(12); // Termina no mês 12 (dezembro) do ano anterior
        } else {
          endDate.setMonth((prevQuarter * 3) + 3);
        }
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'semiannual':
        const currentMonth = now.getMonth();
        endDate.setMonth(currentMonth >= 6 ? 11 : 5);
        endDate.setDate(currentMonth >= 6 ? 31 : 30);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'annual':
        endDate.setMonth(11);
        endDate.setDate(31);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        endDate.setMonth(now.getMonth());
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
    }

    return endDate;
  };

  const getPreviousPeriodDates = (currentStartDate: Date, currentEndDate: Date): { start: Date, end: Date } => {
    const duration = currentEndDate.getTime() - currentStartDate.getTime();
    const previousEnd = new Date(currentStartDate.getTime() - 1); // Um dia antes do início do período atual
    const previousStart = new Date(previousEnd.getTime() - duration);
    
    return { start: previousStart, end: previousEnd };
  };

  const getInsightMessage = () => {
    if (!metrics.trends.shipments || !metrics.trends.expense) return null;

    let message = '';
    let severity: 'success' | 'warning' | 'info' = 'info';

    // Mensagem sobre despesas
    if (metrics.trends.expense.direction === 'down' || metrics.trends.expense.value === 0) {
      message = 'Está num bom caminho, vamos em frente! ';
      severity = 'success';
    } else {
      message = 'Consulte a sua tabela de preços, tente aderir a um plano com mensalidade para ter melhores tarifas! ';
      severity = 'warning';
    }

    // Mensagem sobre envios
    if (metrics.trends.shipments.direction === 'up') {
      message += 'Os seus envios estão a aumentar, continue o excelente trabalho!';
      severity = 'success';
    } else {
      message += 'Considere criar campanhas de marketing para aumentar os seus envios.';
      severity = severity === 'warning' ? 'warning' : 'info';
    }

    return (
      <Alert 
        severity={severity}
        sx={{ 
          mb: 3,
          '& .MuiAlert-message': {
            fontSize: '1rem'
          }
        }}
      >
        {message}
      </Alert>
    );
  };

  const calculateMetrics = (shipments: any[]) => {
    // Filtrar apenas envios válidos
    const validShipments = shipments.filter(shipment => 
      !shipment.tracking_code?.includes('/') && 
      shipment.is_collection === "0"
    ).map(s => ({
      ...s,
      price: parseFloat(s.total_price) || 0,
      weight: parseFloat(s.weight) || 0,
      volumes: parseInt(s.volumes) || 1
    }));

    // Calcular métricas básicas
    const totalShipments = validShipments.length;
    const totalWeight = validShipments.reduce((acc, curr) => acc + curr.weight, 0);
    const totalVolumes = validShipments.reduce((acc, curr) => acc + curr.volumes, 0);
    const totalExpense = validShipments.reduce((acc, curr) => acc + curr.price, 0);

    // Calcular médias
    const averageWeight = totalShipments > 0 ? totalWeight / totalShipments : 0;
    const averageVolumes = totalShipments > 0 ? totalVolumes / totalShipments : 0;
    const averagePrice = totalShipments > 0 ? totalExpense / totalShipments : 0;

    return {
      totalShipments,
      averageWeight,
      averageVolumes,
      totalExpense,
      averagePrice
    };
  };

  const generateDistinctColors = (count: number): string[] => {
    const colors = [
      '#4CAF50', // Verde
      '#2196F3', // Azul
      '#FFC107', // Amarelo
      '#9C27B0', // Roxo
      '#F44336', // Vermelho
      '#FF9800', // Laranja
      '#3F51B5', // Indigo
      '#009688', // Verde água
      '#E91E63', // Rosa
      '#CDDC39', // Lima
      '#00BCD4', // Ciano
      '#FF5722', // Laranja escuro
      '#795548', // Marrom
      '#607D8B', // Azul acinzentado
      '#673AB7', // Roxo escuro
    ];

    // Se precisar de mais cores, gera automaticamente
    if (count > colors.length) {
      for (let i = colors.length; i < count; i++) {
        const hue = Math.floor((i * (360 / count)) % 360);
        colors.push(`hsl(${hue}, 70%, 50%)`);
      }
    }

    return colors.slice(0, count);
  };

  const updateChartData = (shipments: any[]) => {
    // Dados do gráfico de operadores e custos médios
    const operatorData = shipments.reduce((acc, shipment) => {
      const operator = shipment.service?.name || 'Desconhecido';
      if (!acc[operator]) {
        acc[operator] = {
          count: 0,
          totalCost: 0,
          color: OPERATOR_COLORS[Object.keys(acc).length % OPERATOR_COLORS.length]
        };
      }
      acc[operator].count += 1;
      acc[operator].totalCost += parseFloat(shipment.total_price) || 0;
      return acc;
    }, {} as { [key: string]: { count: number; totalCost: number; color: string } });

    const operatorChartData = Object.entries(operatorData)
      .map(([operator, data]) => ({
        label: operator,
        value: data.count,
        color: data.color
      }))
      .sort((a, b) => b.value - a.value);

    const operatorAverageCostData = Object.entries(operatorData)
      .map(([operator, data]) => ({
        label: operator,
        value: data.totalCost / data.count,
        color: data.color
      }))
      .sort((a, b) => b.value - a.value);

    setOperatorChartData(operatorChartData);
    setOperatorAverageCostData(operatorAverageCostData);

    // Dados do gráfico de cidades
    const cityData = shipments.reduce((acc, shipment) => {
      const city = normalizeCity(shipment.recipient_city) || 'Desconhecido';
      if (!acc[city]) {
        acc[city] = {
          count: 0,
          color: OPERATOR_COLORS[Object.keys(acc).length % OPERATOR_COLORS.length]
        };
      }
      acc[city].count += 1;
      return acc;
    }, {} as { [key: string]: { count: number; color: string } });

    const cityChartData = Object.entries(cityData)
      .map(([city, data]) => ({
        id: city,
        label: city,
        value: data.count,
        color: data.color
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 20);

    setCityChartData(cityChartData);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Obter datas do período atual
      const startDate = getStartDate(period);
      const endDate = getEndDate(period);

      // Calcular datas do período anterior
      const previousPeriodDates = getPreviousPeriodDates(startDate, endDate);
      const previousStart = previousPeriodDates.start;
      const previousEnd = previousPeriodDates.end;

      // Buscar todos os envios
      const shipmentsResponse = await shippixService.listShipments(currentUser.companyId, 1, 1000, false);
      const allShipments = Array.isArray(shipmentsResponse) ? shipmentsResponse : shipmentsResponse?.shipments || [];

      // Filtrar recolhas (tracking com "/")
      const shipmentsWithoutPickups = allShipments.filter(shipment => 
        !shipment.tracking_code?.includes('/')
      );

      // Filtrar por data
      const currentShipments = shipmentsWithoutPickups.filter(shipment => {
        const shipmentDate = new Date(shipment.date);
        return shipmentDate >= startDate && shipmentDate <= endDate;
      });

      const previousShipments = shipmentsWithoutPickups.filter(shipment => {
        const shipmentDate = new Date(shipment.date);
        return shipmentDate >= previousStart && shipmentDate <= previousEnd;
      });
      
      setFilteredShipments(currentShipments);

      // Calcular métricas
      const currentMetrics = calculateMetrics(currentShipments);
      const previousMetrics = calculateMetrics(previousShipments);

      // Calcular tendências
      const trends = {
        shipments: calculateTrend(currentMetrics.totalShipments, previousMetrics.totalShipments),
        expense: calculateTrend(currentMetrics.totalExpense, previousMetrics.totalExpense),
        weight: calculateTrend(currentMetrics.averageWeight, previousMetrics.averageWeight),
        volumes: calculateTrend(currentMetrics.averageVolumes, previousMetrics.averageVolumes),
        price: calculateTrend(currentMetrics.averagePrice, previousMetrics.averagePrice)
      };

      // Atualizar métricas com tendências
      setMetrics({
        ...currentMetrics,
        trends
      });

      // Atualizar dados dos gráficos
      updateChartData(currentShipments);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Efeito para recarregar dados quando os filtros mudam
  useEffect(() => {
    loadData();
  }, [period, selectedOperator, selectedCity]);

  const handleOperatorChange = (operator: string) => {
    setSelectedOperator(operator);
  };

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
  };

  const handlePeriodChange = (event: SelectChangeEvent<FilterPeriod>) => {
    setPeriod(event.target.value as FilterPeriod);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await loadData();
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (value: number, decimals: number = 2): string => {
    if (isNaN(value) || !isFinite(value)) return '0';
    return value.toFixed(decimals);
  };

  const MetricCard = ({ title, value, suffix = '', icon: Icon, trend }) => {
    const theme = useTheme();
    
    return (
      <Paper sx={metricCardStyles.paper}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon sx={{ color: `primary.main`, opacity: 0.7 }} />
            <Typography variant="subtitle2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        <Typography variant="h4" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          {typeof value === 'number' ? value.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
          {suffix}
        </Typography>
        {trend && trend.previousValue !== undefined && trend.previousValue > 0 && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {trend.direction === 'up' ? (
                <TrendingUpIcon sx={{ color: 'success.main' }} fontSize="small" />
              ) : (
                <TrendingDownIcon sx={{ color: 'error.main' }} fontSize="small" />
              )}
              <Typography 
                variant="caption" 
                color={trend.direction === 'up' ? 'success.main' : 'error.main'}
                sx={{ fontWeight: 500 }}
              >
                {trend.value}% vs período anterior
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Anterior: {trend.previousValue.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{suffix}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  const filterShipments = (shipments: any[], operator: string, city: string) => {
    return shipments.filter(shipment => {
      const operatorMatch = operator === 'all' || shipment.service?.name === operator;
      const cityMatch = city === 'all' || normalizeCity(shipment.recipient_city) === normalizeCity(city);
      return operatorMatch && cityMatch;
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Grid container spacing={3}>
        {/* Filtros */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Período</InputLabel>
                <Select
                  value={period}
                  onChange={handlePeriodChange}
                  label="Período"
                  startAdornment={
                    <InputAdornment position="start">
                      <CalendarIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="monthly">Mensal</MenuItem>
                  <MenuItem value="current_quarter">Trimestre Atual</MenuItem>
                  <MenuItem value="previous_quarter">Trimestre Anterior</MenuItem>
                  <MenuItem value="semiannual">Semestral</MenuItem>
                  <MenuItem value="annual">Anual</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="operator-select-label">Operador</InputLabel>
                <Select
                  labelId="operator-select-label"
                  id="operator-select"
                  value={selectedOperator}
                  label="Operador"
                  onChange={(e) => handleOperatorChange(e.target.value)}
                >
                  <MenuItem key="all" value="all">Todos os operadores</MenuItem>
                  {Array.from(new Set(filteredShipments.map(s => s.service?.name)))
                    .filter(Boolean)
                    .sort()
                    .map(operator => (
                      <MenuItem key={operator} value={operator}>
                        {operator}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="city-select-label">Cidade</InputLabel>
                <Select
                  labelId="city-select-label"
                  id="city-select"
                  value={selectedCity}
                  label="Cidade"
                  onChange={(e) => handleCityChange(e.target.value)}
                >
                  <MenuItem key="all" value="all">Todas as cidades</MenuItem>
                  {Array.from(new Set(filteredShipments.map(s => normalizeCity(s.recipient_city))))
                    .filter(Boolean)
                    .sort()
                    .map(city => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />

              <Box>
                <Tooltip title="Atualizar dados">
                  <span>
                    <IconButton 
                      onClick={handleRefresh}
                      disabled={isLoading}
                      sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled'
                        }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Mensagem de Insight */}
        <Grid item xs={12}>
          {getInsightMessage()}
        </Grid>

        {/* Widgets */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2.4}>
              <MetricCard
                title="Total de Envios"
                value={metrics.totalShipments}
                icon={LocalShippingIcon}
                trend={metrics.trends.shipments}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <MetricCard
                title="Peso Médio"
                value={metrics.averageWeight}
                suffix=" kg"
                icon={ScaleIcon}
                trend={metrics.trends.weight}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <MetricCard
                title="Média de Volumes"
                value={metrics.averageVolumes}
                icon={InventoryIcon}
                trend={metrics.trends.volumes}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <MetricCard
                title="Despesa Total"
                value={metrics.totalExpense}
                suffix="€"
                icon={AttachMoneyIcon}
                trend={metrics.trends.expense}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <MetricCard
                title="Preço Médio"
                value={metrics.averagePrice}
                suffix="€"
                icon={EuroIcon}
                trend={metrics.trends.price}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Gráficos */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Distribuição por Operador
            </Typography>
            <Box sx={{ height: 300 }}>
              <OperatorBarChart data={operatorChartData} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 2 }}>
              {operatorChartData.map((data) => (
                <Chip
                  key={data.label}
                  label={`${data.label}: ${data.value}`}
                  sx={{
                    backgroundColor: data.color,
                    color: '#fff',
                    '& .MuiChip-label': { fontWeight: 500 }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Custo Médio por Operador
            </Typography>
            <Box sx={{ height: 300 }}>
              <OperatorAverageCostChart data={operatorAverageCostData} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 2 }}>
              {operatorAverageCostData.map((data) => (
                <Chip
                  key={data.label}
                  label={`${data.label}: ${data.value.toFixed(2)}€`}
                  sx={{
                    backgroundColor: data.color,
                    color: '#fff',
                    '& .MuiChip-label': { fontWeight: 500 }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top 20 Cidades
            </Typography>
            <Box sx={{ height: 300 }}>
              <CityPieChart data={cityChartData} />
            </Box>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              justifyContent: 'center',
              mt: 2
            }}>
              {cityChartData.map((city) => (
                <Chip
                  key={city.id}
                  label={`${city.label}: ${city.value}`}
                  sx={{
                    backgroundColor: city.color,
                    color: '#fff',
                    '& .MuiChip-label': { fontWeight: 500 }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
